import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import queryString from "query-string";
import { toast } from "react-toastify";

import api from "libs/api";
import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";

const ClientTasksContext = createContext();

const ClientTasksProvider = ({ children }) => {
  const { id } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    page: _page,
    client_status,
    client_sort,
    client_page,
    ...filters
  } = queryString.parse(search);
  const clientReturnParams = {
    client_status,
    client_sort,
    client_page,
  };
  const default_sort = "updated_at:desc";

  const getTasksUrl = (taskFilters) => {
    const query = queryString.stringify({
      ...clientReturnParams,
      ...taskFilters,
    });

    return `/clients/${id}/tasks${query ? `?${query}` : ""}`;
  };

  const [init_loading, setInitLoading] = useState(true);

  const {
    isLoading: loading,
    data,
    isSuccess,
  } = useQuery({
    queryKey: ["client_tasks", id, filters],
    queryFn: () =>
      api.get(
        `/admin/clients/${id}/tasks?${queryString.stringify({
          ...filters,
          type:
            task_priority_data
              ?.filter((item) =>
                filters?.priority?.split(",").includes(item.key),
              )
              .map((item) => item.value)
              .join(",") || undefined,
          status:
            task_status_data
              ?.filter((item) => filters?.status?.split(",").includes(item.key))
              .map((item) => item.value)
              .join(",") || undefined,
          sort: filters?.sort || default_sort,
        })}`,
      ),
  });

  useEffect(() => {
    if (isSuccess) {
      setInitLoading(false);
    }
  }, [isSuccess]);

  const updateTask = (id, values) =>
    new Promise(async (resolve, reject) => {
      const taskQueries = queryClient.getQueriesData({
        queryKey: ["client_tasks"],
        predicate: (query) => query.queryKey[0] === "client_tasks",
      });

      const previousQueryStates = new Map();

      try {
        taskQueries.forEach(([queryKey, queryData]) => {
          if (!queryData?.tasks) return;

          previousQueryStates.set(queryKey, { ...queryData });

          queryClient.setQueryData(queryKey, (oldData) => {
            if (!oldData?.tasks) return oldData;

            const queryFilters = queryKey[2] || {};

            return {
              ...oldData,
              tasks: oldData.tasks
                .map((task) => {
                  if (task._id === id) {
                    const updatedTask = { ...task, ...values };

                    if (queryFilters.status) {
                      const statusValues = task_status_data
                        ?.filter((item) =>
                          queryFilters.status.split(",").includes(item.key),
                        )
                        .map((item) => item.value);

                      if (
                        statusValues?.length > 0 &&
                        !statusValues.includes(updatedTask.status)
                      ) {
                        return null;
                      }
                    }

                    if (queryFilters.priority) {
                      const typeValues = task_priority_data
                        ?.filter((item) =>
                          queryFilters.priority.split(",").includes(item.key),
                        )
                        .map((item) => item.value);

                      if (
                        typeValues?.length > 0 &&
                        !typeValues.includes(updatedTask.priority)
                      ) {
                        return null;
                      }
                    }

                    return updatedTask;
                  }

                  return task;
                })
                .filter(Boolean),
            };
          });
        });

        await api.put(`/admin/tasks/${id}`, values);

        await queryClient.invalidateQueries({
          queryKey: ["tasks"],
        });

        resolve();
      } catch (err) {
        taskQueries.forEach(([queryKey]) => {
          const originalData = previousQueryStates.get(queryKey);
          if (originalData) {
            queryClient.setQueryData(queryKey, originalData);
          }
        });

        console.error(err);
        toast.error("Something went wrong.");
        reject(err);
      }
    });

  const filterHandler = (name, value, accumulate = false) => {
    let accumulated_value = value;

    if (accumulate) {
      const currentValues = filters[name] ? filters[name].split(",") : [];

      if (value) {
        if (currentValues.includes(value)) {
          const filteredValues = currentValues.filter((val) => val !== value);
          accumulated_value =
            filteredValues.length > 0 ? filteredValues.join(",") : undefined;
        } else {
          currentValues.push(value);
          accumulated_value = currentValues.join(",");
        }
      } else {
        accumulated_value =
          currentValues.length > 0 ? currentValues.join(",") : undefined;
      }
    }

    navigate(
      getTasksUrl({
        ...filters,
        [name]: accumulated_value || undefined,
      }),
    );
  };

  const filterClearAll = () => {
    navigate(getTasksUrl(filters?.sort ? { sort: filters.sort } : {}));
  };

  const applyFilters = (newFilters) => {
    const merged = { ...filters };

    Object.entries(newFilters).forEach(([name, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === false ||
        value === ""
      ) {
        delete merged[name];
      } else {
        merged[name] = typeof value === "boolean" ? value : value?.key;
      }
    });

    navigate(getTasksUrl(merged));
  };

  const value = useMemo(() => {
    return {
      id,
      data,
      loading,
      init_loading,
      filters,
      filterHandler,
      filterClearAll,
      applyFilters,
      updateTask,
    };
    // eslint-disable-next-line
  }, [id, data, loading, init_loading, filters]);

  return (
    <ClientTasksContext.Provider value={value}>
      {children}
    </ClientTasksContext.Provider>
  );
};

const useClientTasks = () => useContext(ClientTasksContext);
export { ClientTasksContext, useClientTasks };
export default ClientTasksProvider;
